// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Human-readable copy for IAM's `permissionAction` strings (e.g.
 * "iam.user.create", "twin.site.read"). There is no catalog of every
 * possible action anywhere upstream or in this repo — each router in
 * `back/app/routers/` hardcodes the literals it checks via
 * `require_permission(...)`, and IAM's own OpenAPI spec only documents the
 * pattern (`^[a-z]+\.[a-z]+(?:\.[a-z]+)?$`), not an enum of values.
 *
 * So this is a best-effort glossary for the actions this repo has actually
 * seen (in the mock IAM data and in `require_permission` call sites), not a
 * source of truth. An action with no entry here still renders — see
 * `describePermission` — just with an auto-generated label instead of
 * hand-written copy. Extend this table as new actions turn up.
 */
const KNOWN_PERMISSIONS: Record<string, { title: string; description: string }> = {
  'iam.user.create': { title: 'Invite users', description: 'Create new user accounts' },
  'iam.user.read': { title: 'View users', description: 'See user profiles and access' },
  'iam.user.update': { title: 'Edit users', description: "Change a user's role or profile" },
  'iam.user.delete': { title: 'Remove users', description: 'Deactivate or delete a user account' },
  'iam.role.read': { title: 'View roles', description: "See a role's permissions" },
  'iam.role.create': {
    title: 'Create roles',
    description: 'Define a new role and its permissions',
  },
  'iam.role-assignment.read': {
    title: 'View role assignments',
    description: 'See who holds which role, and where',
  },
  'twin.site.read': {
    title: 'View telemetry',
    description: 'Live values, trends and the floor plan',
  },
  'site.read': { title: 'View site data', description: 'Read-only access to site data' },
};

/** Turns an unrecognized `permissionAction` into readable fallback copy,
 *  e.g. "twin.alarm.ack" -> "Twin alarm ack". Better than hiding it. */
function humanize(action: string): string {
  const words = action.split('.').join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function permissionTitle(action: string): string {
  return KNOWN_PERMISSIONS[action]?.title ?? humanize(action);
}

export function permissionDescription(action: string): string {
  return KNOWN_PERMISSIONS[action]?.description ?? action;
}
